export type ServiceLayer = 'PINCODE' | 'PDS' | 'TNEB' | 'CONSTITUENCY' | 'POLICE' | 'HEALTH' | 'LOCAL_BODIES_V2' | 'VETERINARY' | 'REGISTRATION' | 'LAND_PARCEL' | 'TEMPLES' | 'RADIO' | 'FUEL';

export type Position = [number, number];
export type BBox = [number, number, number, number];

export interface Geometry {
  type: 'Point' | 'MultiPoint' | 'LineString' | 'MultiLineString' | 'Polygon' | 'MultiPolygon';
  coordinates: unknown;
}

export interface Point extends Geometry {
  type: 'Point';
  coordinates: Position;
}

export interface MultiPoint extends Geometry {
  type: 'MultiPoint';
  coordinates: Position[];
}

export interface LineString extends Geometry {
  type: 'LineString';
  coordinates: Position[];
}

export interface MultiLineString extends Geometry {
  type: 'MultiLineString';
  coordinates: Position[][];
}

export interface Polygon extends Geometry {
  type: 'Polygon';
  coordinates: Position[][];
}

export interface MultiPolygon extends Geometry {
  type: 'MultiPolygon';
  coordinates: Position[][][];
}

export interface GisProperties {
  [key: string]: string | number | boolean | null | undefined | Position | string[];
  district?: string;
  office_name?: string;
  PIN_CODE?: string | number;
  pincode?: string | number | null;
  NAME?: string;
  office_typ?: string;
  region_nam?: string;
  // Registration properties
  office_nam?: string;
  zone_name?: string;
  district_n?: string;
  sro_name?: string;
  dig_name?: string;
  dro_name?: string;
  match_confidence?: 'high' | 'medium' | 'low' | null;
}

export interface RegistrationOfficeProperties extends GisProperties {
  block_name: string;
  district_n: string;
  office_nam: string;
  office_typ: string;
  revenue_vi: string;
  taluk_name: string;
  zone_name: string;
}

export interface RegistrationBoundaryProperties extends GisProperties {
  dig_name: string;
  dro_name: string;
  gid: number;
  ogc_fid: number;
  sro_id: string;
  sro_name: string;
}

export interface GisFeature<G extends Geometry = Geometry, P = GisProperties> {
  type: 'Feature';
  geometry: G;
  properties: P;
  bbox?: BBox;
  id?: string | number;
  suggestionType?: 'PINCODE' | 'PDS_SHOP' | 'TNEB_SECTION' | 'DISTRICT' | 'CONSTITUENCY' | 'POLICE_STATION' | 'HEALTH_FACILITY' | 'VETERINARY_FACILITY' | 'TEMPLE_FACILITY' | 'COORDINATES' | 'GLOBAL_PLACE' | 'RADIO_STATION' | 'FUEL_STATION';
}

export interface RadioStationProperties extends GisProperties {
  id: string;
  name: string;
  city: string;
  streamId: string;
  logoUrl: string;
}

export type RadioStation = GisFeature<Point, RadioStationProperties>;

export interface VeterinaryFacilityProperties extends GisProperties {
  district_n: string;
  taluk_name: string;
  village_na: string;
  division: string;
  type: 'POLY_CLINIC' | 'CLINIC_CENTER' | 'HOSPITAL' | 'DISPENSARY' | 'SUB_CENTER';
}

export type VeterinaryFacility = GisFeature<Point, VeterinaryFacilityProperties>;

export interface LandParcelProperties extends GisProperties {
  district_name: string;
  district_tamil_name: string;
  taluk_name: string;
  taluk_tamil_name: string;
  village_name: string;
  village_tamil_name: string;
  survey_number: string;
  sub_division: string;
  ulpin: string;
  centroid: string;
  area?: string | number;
  rural_urban?: 'rural' | 'urban';
  revenue_town_name?: string;
  revenue_town_tamil_name?: string;
  revenue_ward_name?: string;
  revenue_ward_tamil_name?: string;
  revenue_block_name?: string;
  revenue_block_tamil_name?: string;
}

export type LandParcel = GisFeature<Polygon | MultiPolygon, LandParcelProperties>;

export interface ConstituencyProperties extends GisProperties {
  district_n?: string; // For AC
  assembly_c?: string; // For AC name
  parliame_1?: string; // For both AC (parent) and PC (name)
}

export interface GisFeatureCollection<G extends Geometry = Geometry, P = GisProperties> {
  type: 'FeatureCollection';
  features: GisFeature<G, P>[];
  bbox?: BBox;
}

export interface PdsProperties extends GisProperties {
  shop_code: string;
  name: string;
  village: string;
  taluk: string;
  district: string;
  coords: Position;
  incharge?: string;
  contact_no?: string;
  area_type?: string;
  num_cards?: number;
  num_beneficiaries?: number;
  address?: string;
}

export type PdsShop = GisFeature<Point, PdsProperties>;

export interface TnebProperties extends GisProperties {
  section_na?: string;
  section_office?: string;
  section_co?: string | number;
  subdivisio?: string;
  sub_division?: string;
  subdivis_1?: string | number;
  sub_div_co?: string | number;
  division_n?: string;
  division?: string;
  division_c?: string | number;
  div_cod?: string | number;
  circle_nam?: string;
  circle?: string;
  circle_cod?: string | number;
  region_nam?: string;
  region?: string;
  region_id?: string | number;
  region_cod?: string | number;
  mobile?: string;
  email?: string;
  returned_office?: string;
}

export interface TnebSection extends TnebProperties {
  office_location?: Position;
}

export interface PoliceBoundaryProperties extends GisProperties {
  police_s_1: string; // Key (PS Code)
  police_sta: string; // Name alias 1
  police_s_2?: string; // Name alias 2
  police_dis: string;
  taluk_name: string;
  district_n: string;
}

export type PoliceMatchConfidence = 'exact' | 'high' | 'medium' | 'low' | 'unresolved';

export interface PoliceMatchDebug {
  boundaryId: string; // ciprus_loc or derived
  boundaryCode: string;
  boundaryAliases: string[];
  stationCode?: string;
  stationAliases?: string[];
  inferredStationCode?: string;
  codeMatch: boolean;
  aliasMatchStrength: number;
  isInsideBoundary: boolean;
  distanceToClick: number;
  distanceToCentroid: number;
  overrideUsed: boolean;
  confidence: PoliceMatchConfidence;
  reason: string;
  method: string;
}

export interface PoliceResolutionResult {
  boundary: GisFeature<Polygon | MultiPolygon, PoliceBoundaryProperties>;
  station: GisFeature<Point, PoliceStationProperties> | null;
  confidence: PoliceMatchConfidence;
  reason: string;
  debug: PoliceMatchDebug;
  isBoundaryValid?: boolean;
  validationError?: string;
  // Chennai specific fields
  chennaiResult?: ChennaiPoliceResolutionResult;
}

export interface ChennaiPoliceResolutionResult {
  stationCode: string | null;
  stationName: string | null;
  matchedBoundary: GisFeature<Polygon | MultiPolygon, PoliceBoundaryProperties> | null;
  confidence: number;
  matchType: string;
  insidePolygon: boolean;
  distanceKm: number;
  warnings: string[];
  primaryStation: GisFeature<Point, PoliceStationProperties> | null;
  relatedStations: GisFeature<Point, PoliceStationProperties>[];
}

export interface PoliceStationProperties extends GisProperties {
  ps_code: string; // Key
  ps_name: string; // Name
  name: string;    // Full Name
  status: boolean;
  station_location?: Position;
  district?: string;
  taluk?: string;
}
export interface PostalOffice {
  circlename: string;
  regionname: string;
  divisionname: string;
  officename: string;
  pincode: string;
  officetype: string;
  delivery: string;
  district: string;
  statename: string;
  latitude: string | number;
  longitude: string | number;
  isOutlier?: boolean;
  outlierReason?: string;
}

export interface HealthFacilityProperties extends GisProperties {
  reference_?: string | number;
  facility_n: string;
  facility_t: string;
  nin_number?: string | number;
  district_n: string;
  sub_distri?: string;
  block_name?: string;
  location_t?: string;
  timing_of_?: string;
  phc_catego?: string;
  fru?: string;
  under_heal?: string;
  hwc?: string;
  kayakalp?: string;
  nqas?: string;
  delivery_p?: string;
  blood_bank?: string;
  blood_stor?: string;
  ct?: string;
  mri?: string;
  dialysis_c?: string;
  sncu?: string;
  nbsu?: string;
  deic?: string;
  cbnaat_sit?: string;
  tele_v_car?: string;
  stemi_hubs?: string;
  stemi_spok?: string;
  cath_lab_m?: string;
  prem_centr?: string;
  script_hub?: string;
  script_spo?: string;
}

export type HealthFacility = GisFeature<Point, HealthFacilityProperties>;

export interface HealthDistrictSummary {
  district: string;
  file_name: string;
  total: number;
  by_type: Record<string, number>;
  location: Record<string, number>;
  capabilities: Record<string, number>;
}

export interface HealthManifest {
  generated_at: string;
  total_facilities: number;
  district_count: number;
  priority_types: string[];
  capability_fields: string[];
  statewide: {
    by_type: Record<string, number>;
    location: Record<string, number>;
    capabilities: Record<string, number>;
    priority_total: number;
  };
  districts: HealthDistrictSummary[];
}
export type HealthScope = 'STATE' | 'DISTRICT' | 'PINCODE';

export interface HealthFilters {
  facilityTypes: string[]; // e.g., ['PHC', 'CHC', 'MCH', 'DH', 'SDH', 'HSC']
  locationType: 'Urban' | 'Rural' | 'All';
  isHwc: boolean | null;
  hasDelivery: boolean | null;
  isFru: boolean | null;
  is24x7: boolean | null;
  // Maternal / Emergency
  hasBloodBank: boolean | null;
  hasBloodStorage: boolean | null;
  // Child / Neonatal
  hasSncu: boolean | null;
  hasNbsu: boolean | null;
  hasDeic: boolean | null;
  // Diagnostics / Specialty
  hasCt: boolean | null;
  hasMri: boolean | null;
  hasDialysis: boolean | null;
  hasCbnaat: boolean | null;
  hasTeleConsultation: boolean | null;
  // Cardiac / Advanced
  hasStemiHub: boolean | null;
  hasStemiSpoke: boolean | null;
  hasCathLab: boolean | null;
}

export interface HealthSummary {
  name: string;
  scope: HealthScope;
  total: number;
  countsByType: Record<string, number>;
  countsByCapability: Record<string, number>;
  activeFilters: string[];
  district?: string;
  pincode?: string;
}

export interface ElectionResult {
  party: string;
  candidate: string;
  color: string;
  status: 'LEADING' | 'WON' | string;
  constituencyId: number;
}

export const PARTY_COLORS: Record<string, string> = {
  'ADMK': '#A08547',
  'DMK': '#05F86E',
  'TVK': '#e72bd9',
  'CPI': '#e70d21',
  'BJP': '#ff9933',
  'INC': '#19AAED',
  'NTK': '#800000',
  'PMK': '#ffff00',
  'VCK': '#0000ff',
  'OTHERS': '#94a3b8'
};

export interface TempleHistory {
  cen?: string;    // Century/Period
  her?: string;    // Heritage flag ("Yes" / "No")
  vim?: string[];  // Vimana type
  sha?: string;    // Sanctorum shape
  pad?: string;    // Padal pettrathu
  king?: string;   // King/dynasty period
  tree?: string;   // Sthala virutcham (sacred tree)
  agm?: string[];  // Aagamam rules
  vrs?: string;    // Verses
  saint?: string;  // Poet saints
  spec?: string;   // Sthala specialty type
}

export interface TempleProperties {
  id: string;      // Unique Temple ID (e.g. TM009791)
  no: number;      // Serial Number
  nm: {
    en: string;
    tn: string;
  };
  dist: {
    en: string;
    tn: string;
  };
  kat: 'Y' | 'N';  // Kattalai flag
  g: {
    lat: string | number;
    lng: string | number;
    dc: string;    // District Code
    tc: string;    // Taluk Code
    vc: string;    // Village Code
  };
  c: {
    addr: {
      en: string;
      tn: string;
    };
    mol: {
      en: string;
      tn: string;
    };
    amb: {
      en: string;
      tn: string;
    };
    hrs: {
      sl: string[];
      d: {
        en: string;
        tn: string;
      };
    };
    pj: {
      en: Array<{ name: string; time: string }>;
      tn: Array<{ name: string; time: string }>;
    };
    sp: {
      en: Array<{ title: string; description: string }>;
      tn: Array<{ title: string; description: string }>;
    };
    sub: {
      en: Array<{ title: string; description: string }>;
      tn: Array<{ title: string; description: string }>;
    };
    svc: {
      en: Array<{ title: string; description: string }>;
      tn: Array<{ title: string; description: string }>;
    };
    fac: {
      en: Array<{ title: string; description: string }>;
      tn: Array<{ title: string; description: string }>;
    };
    hist?: {
      en?: TempleHistory;
      tn?: TempleHistory;
    };
  };
  importance_score?: number;
  tier?: 'T1' | 'T2' | 'T3';
  transaction_count?: number;
  device_count?: number;
}

export interface PosTempleSummary {
  device_count: number;
  transaction_count: number;
  banks: string[];
  last_updated: string;
}

export type FuelGroupId = 'PETROL' | 'DIESEL' | 'PREMIUM' | 'E0' | 'EV';

export type FuelScope = 'STATE' | 'DISTRICT' | 'PINCODE';

export interface FuelStationProperties extends GisProperties {
  id: string;
  brand: string;
  name: string;
  address?: string;
  district: string;
  city?: string | null;
  pincode?: string | number | null;
  amenities?: string[];
  fuels?: string[];
  /**
   * False when no grade data was ever collected for this station — every Nayara and
   * Jio-bp outlet, plus outlets whose price endpoint returned an empty body. An empty
   * `fuels` with `fuelsKnown: false` means *unknown*, never *sells nothing*, and must
   * not be rendered as "no fuel available".
   */
  fuelsKnown?: boolean;
  /** Canonical groups derived from `fuels`; present on every rendered feature. */
  fuelGroups?: FuelGroupId[];
  hours?: string | null;
  contact?: string | null;
  sourceUrl?: string | null;
}

export type FuelStation = GisFeature<Point, FuelStationProperties>;

export interface FuelFilters {
  brands: string[];
  fuelGroups: FuelGroupId[];
}

export type FuelCounts = Record<string, number>;

export interface FuelDistrictManifestEntry {
  district: string;
  file_name: string;
  total: number;
  by_brand: FuelCounts;
  by_fuel_group: FuelCounts;
  /** Stations with no grade data at all — see `FuelStationProperties.fuelsKnown`. */
  unknown_grades: number;
}

export interface FuelManifest {
  generated_at: string;
  source_file: string;
  total_stations: number;
  district_count: number;
  brands: string[];
  statewide: {
    by_brand: FuelCounts;
    by_fuel_group: FuelCounts;
    /** Stations with no grade data at all — see `FuelStationProperties.fuelsKnown`. */
    unknown_grades: number;
  };
  districts: FuelDistrictManifestEntry[];
}

/**
 * Packed statewide index row, shared by search suggestions and STATE scope
 * rendering: `[id, name, district, brand, lat, lng, fuelGroupMask]`.
 */
export type FuelIndexEntry = [string, string, string, string, number, number, number];

export interface FuelSummary {
  name: string;
  scope: FuelScope;
  total: number;
  countsByBrand: FuelCounts;
  countsByFuelGroup: FuelCounts;
  district: string | null;
  pincode: string | null;
}

